import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Download, 
  Users, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  BarChart3 
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import { api } from '../../api';

const AdminQuizView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [quiz, setQuiz] = useState<any>(null);
  const [attempts, setAttempts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAttempt, setSelectedAttempt] = useState<any>(null);
  const [showAttemptModal, setShowAttemptModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  useEffect(() => {
    loadQuiz();
    loadAttempts();
  }, [id]);

  const loadQuiz = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/quizzes/${id}`);

      setQuiz(response.data.quiz);
      console.log('Fetched Quiz:', response.data.quiz);
      
      // Expand the first section by default
      if (response.data.quiz.sections && response.data.quiz.sections.length > 0) {
        setExpandedSections([response.data.quiz.sections[0]._id]);
      }
    } catch (error) {
      console.error('Load quiz error:', error);
      toast.error('Erreur lors du chargement du quiz');
      navigate('/admin/quizzes');
    } finally {
      setLoading(false);
    }
  };

  const loadAttempts = async () => {
    try {
      const response = await api.get(`/quizzes/${id}/attempts`);
      setAttempts(response.data.attempts);
    } catch (error) {
      console.error('Load attempts error:', error);
      toast.error('Erreur lors du chargement des tentatives');
    }
  };

  const handleDeleteQuiz = async () => {
    try {
      await api.delete(`/quizzes/${id}`);
      toast.success('Quiz supprimé avec succès');
      navigate('/admin/quizzes');
    } catch (error) {
      console.error('Delete quiz error:', error);
      toast.error('Erreur lors de la suppression du quiz');
    }
  };

  const handleViewAttempt = (attempt: any) => {
    setSelectedAttempt(attempt);
    setShowAttemptModal(true);
  };

  const exportResults = () => {
    // Create CSV content
    const headers = ['Étudiant', 'Email', 'Score', 'Date', 'Temps passé', 'Statut'];
    const rows = attempts.map(attempt => [
      attempt.student?.name || 'Inconnu',
      attempt.student?.email || 'Inconnu',
      `${attempt.score}%`,
      new Date(attempt.submittedAt).toLocaleDateString('fr-FR'),
      `${attempt.timeSpent} min`,
      attempt.isPassed ? 'Réussi' : 'Échoué'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `resultats_quiz_${id}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getScoreColor = (score: number, passingScore: number) => {
    if (score >= passingScore) return 'text-brand-600';
    if (score >= passingScore * 0.8) return 'text-brand-600';
    return 'text-brand-600';
  };

  const getScoreDistribution = () => {
    const distribution = [
      { range: '0-20%', count: 0 },
      { range: '21-40%', count: 0 },
      { range: '41-60%', count: 0 },
      { range: '61-80%', count: 0 },
      { range: '81-100%', count: 0 }
    ];
    
    attempts.forEach(attempt => {
      if (attempt.score <= 20) distribution[0].count++;
      else if (attempt.score <= 40) distribution[1].count++;
      else if (attempt.score <= 60) distribution[2].count++;
      else if (attempt.score <= 80) distribution[3].count++;
      else distribution[4].count++;
    });
    
    return distribution;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
        </div>
      </Layout>
    );
  }

  if (!quiz) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <AlertCircle className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Quiz non trouvé</h3>
          <p className="text-gray-500 mb-4">
            Le quiz que vous recherchez n'existe pas ou a été supprimé.
          </p>
          <button
            onClick={() => navigate('/admin/quizzes')}
            className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            Retour à la liste des quiz
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/admin/quizzes')}
              className="mr-4 text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900">{quiz.title}</h1>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate(`/admin/quizzes/${id}/edit`)}
              className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              <Edit className="h-4 w-4 mr-2" />
              Modifier
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer
            </button>
          </div>
        </div>

        {/* Quiz Overview */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations du quiz</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Cours:</span>
                  <span className="font-medium">{quiz.course?.title || 'Non spécifié'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Questions:</span>
                  <span className="font-medium">{quiz.questions?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Temps limite:</span>
                  <span className="font-medium">{quiz.settings?.timeLimit || 30} minutes</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tentatives max:</span>
                  <span className="font-medium">{quiz.settings?.maxAttempts || 3}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Score de passage:</span>
                  <span className="font-medium">{quiz.settings?.passingScore || 70}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Difficulté:</span>
                  <span className="font-medium">{quiz.difficulty === 'facile' ? 'Facile' : 
                                                quiz.difficulty === 'moyen' ? 'Moyen' : 
                                                quiz.difficulty === 'difficile' ? 'Difficile' : 
                                                quiz.difficulty}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Statut:</span>
                  <span className="font-medium">{quiz.isActive ? 'Actif' : 'Inactif'}</span>
                </div>
              </div>
            </div>
            
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Statistiques</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-brand-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-brand-600">{quiz.analytics?.totalAttempts || 0}</div>
                  <div className="text-sm text-brand-800">Tentatives totales</div>
                </div>
                <div className="bg-brand-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-brand-600">{quiz.analytics?.averageScore?.toFixed(1) || 0}%</div>
                  <div className="text-sm text-brand-800">Score moyen</div>
                </div>
                <div className="bg-brand-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-brand-600">{quiz.analytics?.passRate?.toFixed(1) || 0}%</div>
                  <div className="text-sm text-brand-800">Taux de réussite</div>
                </div>
                <div className="bg-brand-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-brand-600">{quiz.analytics?.averageTimeSpent || 0} min</div>
                  <div className="text-sm text-brand-800">Temps moyen</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Score Distribution */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Distribution des scores</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={getScoreDistribution()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#2D8659" name="Nombre d'étudiants" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Attempts List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Tentatives ({attempts.length})</h2>
            <button
              onClick={exportResults}
              disabled={attempts.length === 0}
              className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4 mr-2" />
              Exporter les résultats
            </button>
          </div>

          {attempts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Étudiant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Temps
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tentative
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attempts.map((attempt) => (
                    <tr key={attempt._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-brand-600 rounded-full flex items-center justify-center text-white">
                            {attempt.student?.name?.charAt(0).toUpperCase() || 'S'}
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{attempt.student?.name || 'Étudiant inconnu'}</div>
                            <div className="text-xs text-gray-500">{attempt.student?.email || 'Email inconnu'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-medium ${getScoreColor(attempt.score, quiz.settings?.passingScore || 70)}`}>
                          {attempt.score}%
                        </div>
                        <div className="text-xs text-gray-500">
                          {attempt.pointsEarned}/{attempt.totalPoints} points
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {attempt.timeSpent} min
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(attempt.submittedAt).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        #{attempt.attemptNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewAttempt(attempt)}
                          className="text-brand-600 hover:text-brand-900"
                        >
                          Détails
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-4">
                <Users className="h-16 w-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune tentative</h3>
              <p className="text-gray-500">
                Aucun étudiant n'a encore tenté ce quiz.
              </p>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Confirmer la suppression</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700">
                  Êtes-vous sûr de vouloir supprimer ce quiz ? Cette action est irréversible et supprimera également toutes les tentatives associées.
                </p>
              </div>
              <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteQuiz}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Attempt Detail Modal */}
        {showAttemptModal && selectedAttempt && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">
                      Détails de la tentative
                    </h2>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-600">
                        {selectedAttempt.student?.name || 'Étudiant inconnu'}
                      </span>
                      <span className="text-sm text-gray-600">
                        Tentative #{selectedAttempt.attemptNumber}
                      </span>
                      <span className="text-sm text-gray-600">
                        {new Date(selectedAttempt.submittedAt).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowAttemptModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className={`rounded-lg p-4 ${
                    selectedAttempt.isPassed ? 'bg-brand-50' : 'bg-brand-50'
                  }`}>
                    <div className={`text-2xl font-bold ${
                      selectedAttempt.isPassed ? 'text-brand-600' : 'text-brand-600'
                    }`}>
                      {selectedAttempt.score}%
                    </div>
                    <div className="text-sm text-gray-600">
                      {selectedAttempt.pointsEarned}/{selectedAttempt.totalPoints} points
                    </div>
                    <div className="text-sm mt-1">
                      {selectedAttempt.isPassed ? (
                        <span className="text-brand-600 flex items-center">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Réussi
                        </span>
                      ) : (
                        <span className="text-brand-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          Échoué
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="bg-brand-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-brand-600">{selectedAttempt.timeSpent} min</div>
                    <div className="text-sm text-brand-800">Temps passé</div>
                  </div>
                  <div className="bg-brand-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-brand-600">
                      {selectedAttempt.answers?.filter((a: any) => a.isCorrect).length || 0}/
                      {selectedAttempt.answers?.length || 0}
                    </div>
                    <div className="text-sm text-brand-800">Réponses correctes</div>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-4">Réponses</h3>
                <div className="space-y-4">
                  {selectedAttempt.answers?.map((answer: any, index: number) => {
                    // Find the corresponding question
                    const question = quiz.questions.find((q: any) => q._id === answer.question);
                    if (!question) return null;

                    return (
                      <div key={index} className={`p-4 rounded-lg border ${
                        answer.isCorrect ? 'border-brand-200 bg-brand-50' : 'border-brand-200 bg-brand-50'
                      }`}>
                        <div className="flex items-start">
                          <div className={`p-1 rounded-full ${
                            answer.isCorrect ? 'bg-brand-200' : 'bg-brand-200'
                          } mr-3 mt-1`}>
                            {answer.isCorrect ? (
                              <CheckCircle className="h-4 w-4 text-brand-600" />
                            ) : (
                              <AlertCircle className="h-4 w-4 text-brand-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{question.question}</div>
                            <div className="text-sm mt-2">
                              <div className="mb-1">
                                <span className="font-medium">Réponse de l'étudiant:</span>
                                <span className="ml-2">{
                                  Array.isArray(answer.answer) 
                                    ? answer.answer.map((a: string) => {
                                        const option = question.options.find((o: any) => o._id === a);
                                        return option ? option.text : a;
                                      }).join(', ')
                                    : answer.answer
                                }</span>
                              </div>
                              
                              {!answer.isCorrect && (
                                <div className="text-brand-600">
                                  <span className="font-medium">Réponse correcte:</span>
                                  <span className="ml-2">{
                                    question.type === 'multiple-choice' || question.type === 'true-false'
                                      ? question.options.filter((o: any) => o.isCorrect).map((o: any) => o.text).join(', ')
                                      : question.correctAnswer
                                  }</span>
                                </div>
                              )}
                              
                              {question.explanation && (
                                <div className="mt-2 p-2 bg-gray-100 rounded text-sm text-gray-700">
                                  <span className="font-medium">Explication:</span> {question.explanation}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-sm font-medium ml-4">
                            {answer.points}/{question.points} pts
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setShowAttemptModal(false)}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminQuizView;